// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { collection, getDocs, query } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import AdminHome from './components/AdminHome';
import GroupManagement from './components/GroupManagement/GroupManagement';
import ProgramManagement from './components/ProgramManagement/ProgramManagement';
import UserManagement from '../../components/admin/UserManagement';
import '../../styles/adminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({ totalUsers: 0, totalGroups: 0, totalAssignments: 0, activeAssignments: 0 });
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const { currentUser, userRole } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || userRole !== 'admin') return;
      try {
        setError(null);
        if (auth.currentUser) {
          try {
            const usersSnapshot = await getDocs(query(collection(db, 'users')));
            const groupsSnapshot = await getDocs(query(collection(db, 'groups')));
            const assignmentsSnapshot = await getDocs(query(collection(db, 'assignments')));
            const groupsData = [];
            groupsSnapshot.forEach(doc => groupsData.push({ id: doc.id, ...doc.data() }));
            setGroups(groupsData);
            let activeAssignmentCount = 0;
            assignmentsSnapshot.forEach(doc => { if (doc.data().status === 'open') activeAssignmentCount++; });
            setStats({ totalUsers: usersSnapshot.size, totalGroups: groupsSnapshot.size, totalAssignments: assignmentsSnapshot.size, activeAssignments: activeAssignmentCount });
          } catch (collectionError) {
            console.error("Error accessing collections:", collectionError);
            setError("Failed to load dashboard statistics. Please try refreshing the page.");
          }
        } else {
          setError("Authentication error. Please log out and log back in.");
        }
      } catch (error) {
        console.error("Error in fetchData:", error);
        setError("Failed to load dashboard statistics. Please try refreshing the page.");
      }
    };
    const timer = setTimeout(() => fetchData(), 1500);
    return () => clearTimeout(timer);
  }, [currentUser, userRole]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/users')) setActiveTab('users');
    else if (path.includes('/groups')) setActiveTab('groups');
    else if (path.includes('/programs')) setActiveTab('programs');
    else setActiveTab('overview');
  }, [location.pathname]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    switch (tab) {
      case 'overview': navigate('/admin'); break;
      case 'users':    navigate('/admin/users'); break;
      case 'groups':   navigate('/admin/groups'); break;
      case 'programs': navigate('/admin/programs'); break;
      default:         navigate('/admin');
    }
  };

  const handleRetry = () => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(() => window.location.reload()).catch(() => setError("Authentication error. Please log out and log back in."));
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="admin-dashboard">
      <h1>Assignment Management</h1>
      <div className="dashboard-tabs">
        <ul>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'users',    label: 'Users' },
            { key: 'groups',   label: 'Groups' },
            { key: 'programs', label: 'Programs' },
          ].map(({ key, label }) => (
            <li key={key} className={activeTab === key ? 'active' : ''} onClick={() => handleTabClick(key)}>
              {label}
            </li>
          ))}
        </ul>
      </div>
      {error && <div className="error-message">{error}<button onClick={handleRetry}>Retry</button></div>}
      <div className="dashboard-content">
        <Routes>
          <Route index element={<AdminHome stats={stats} error={error} onRetry={handleRetry} />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="groups" element={<GroupManagement />} />
          <Route path="programs" element={<ProgramManagement groups={groups} />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default AdminDashboard;